import Vue from "vue";
import Vuex from "vuex";
import "firebase/auth";
import * as backend from "@/domain/backend";
import * as util from "@/util";
import {
  RootState,
  Company,
  PartialPermission,
  PERMISSION_ATTRS,
  SPECIAL_PERMISSION_ATTRS,
  WebsiteStore,
  langKey,
  LangDataOption,
  User,
  UserCustomClaims
} from "./types";
import lodash from "lodash";
import router, { getUserStartPage, routeEquals, simplifyRoute } from "@/modules/router";
import { isNull } from "@/util";
import { getLangData } from "@/lib/language";

Vue.use(Vuex);

const initialLang: langKey = localStorage.getItem("langKey") === "EN" ? "EN" : "ES";

interface WebsiteGetters {
  isInCompany: boolean;
  isLoadingUser: boolean;
  validCompanies: boolean;
  userPermission: PartialPermission;
}

const storeOptions: WebsiteStore = {
  state: {
    currentUser: null,
    currentUserLoading: false,
    needsReinforcedLogin: false,
    reinforcedLoginNotice: "",
    redirectAfterLogin: "",
    lastError: null,
    selectedCompanyLoading: false,
    selectedCompany: null,
    userCompaniesLoading: false,
    userCompanies: null,
    lang: getLangData(initialLang),
    branch: null
  },
  mutations: {
    setUser(state, data: { newUser?: User; loading?: boolean; error?: Error | null; logout?: boolean }) {
      util.log("State: setUser", { data });
      state.currentUserLoading = !!data.loading;
      if (data.logout || data.newUser) {
        state.currentUser = data.newUser ?? null;
        state.selectedCompanyLoading = false;
        state.selectedCompany = null;
        state.userCompaniesLoading = state.currentUserLoading;
        state.userCompanies = null;
      }
      state.lastError = data.error;
    },
    setUserClaims(state, claims: Partial<UserCustomClaims>) {
      util.log("State: setUser", { claims });
      if (state.currentUser) {
        state.currentUser = {
          ...state.currentUser,
          claims
        };
      }
    },
    setNeedsReinforced(state, value: boolean) {
      util.log("State: setNeedsReinforced", { value });
      state.needsReinforcedLogin = value;
    },
    setReinforcedLoginNotice(state, message: string) {
      util.log("State: setReinforcedLoginNotice", { message });
      state.reinforcedLoginNotice = message;
    },
    setRedirect(state, url) {
      util.log("State: setRedirect", { url });
      state.redirectAfterLogin = url;
    },
    setBranch(state, id) {
      util.log("State: setBranch", { id });
      state.branch = id;
    },
    updateProfile(state, newProfile) {
      util.log("State: updateProfile", newProfile);
      if (state.currentUser) {
        state.currentUser.name = newProfile.displayName;
      }
    },
    setCompanies(state, data: { companies?: Company[] | null; loading?: boolean; error?: Error }) {
      util.log("State: setCompanies", data);
      state.userCompaniesLoading = !!data.loading;
      if (data.companies !== undefined) {
        sortBranchesInCompanies(...(data.companies ?? []));
        state.userCompanies = auxSortByName(data.companies ?? []);
      }
      state.lastError = data.error;
    },
    setCompany(state, data: { company?: Company | null; loading?: boolean; error?: Error }) {
      util.log("State: setCompany", data);
      state.selectedCompanyLoading = !!data.loading;
      if (data.company !== undefined) {
        sortBranchesInCompanies(data.company ?? null);
        state.selectedCompany = data.company;
      }
      state.lastError = data.error;
    },
    changeLang: (state, value: LangDataOption) => {
      util.log("State: changeLang", value);
      state.lang = value;
    }
  },
  actions: {
    async login(_context, { email, password, rememberMe }) {
      util.log("Action: login email", { email, rememberMe });
      await backend.login(email, password, rememberMe);
    },
    async loginWithToken({ commit }, paytefToken) {
      util.log("Action: login token");
      const tokenValidation = await backend.validateToken(paytefToken);
      return {
        tokenValidation,
        next: async () => {
          commit("setRedirect", {
            path: tokenValidation.companyExists ? getUserStartPage() : "/usermanager",
            params: { companyID: tokenValidation.companyID.toString() }
          });
          await backend.loginWithToken(tokenValidation.authToken);
        }
      };
    },
    async loadUser(context, fbPartialUser: User | null) {
      util.log("Action: loadUser", fbPartialUser);
      if (!fbPartialUser) {
        context.commit("setUser", { logout: true, loading: false });
        return;
      }
      try {
        context.commit("setUser", { newUser: fbPartialUser, loading: true });
        const userData = await backend.getUserData();
        const claims = await backend.refreshCurrentToken();
        const needsReinforced = !!userData.requiresReinforcedLogin || !!claims.requiresReinforcedLogin;
        util.log({ claims });
        const newUser: User = {
          ...fbPartialUser,
          ...userData,
          isLoading: false,
          needsReinforced,
          claims
        };
        context.commit("setUser", {
          newUser,
          loading: false
        });

        if (needsReinforced && !claims.reinforcedLogin) {
          context.commit("setNeedsReinforced", true);
          await backend.reinforceSend();
          await router.replace("/authreinforce");
          return;
        }
        await context.dispatch("finishSignIn");
      } catch (error) {
        util.logError("loadUser", error);
        context.commit("setUser", { error });
      }
    },
    async finishSignIn(context) {
      util.log("Action: finishSignIn");
      await context.dispatch("refreshCompanies");
      await context.dispatch("redirect");
    },
    async completeReinforcedLogin(context, code: string | null) {
      util.log("Action: completeReinforcedLogin");
      // If code is null the user skipped the verification
      if (code != null) {
        await backend.reinforceVerify(code.trim());
      }
      const claims = await backend.refreshCurrentToken();
      context.commit("setUserClaims", claims);
      context.commit("setNeedsReinforced", false);
      await context.dispatch("finishSignIn");
    },
    async redirect(context) {
      util.log("Action: redirect");
      if (context.state.needsReinforcedLogin) {
        util.log("Can't redirect because user needs to do reinforced auth, navigating there...");
        await router.replace("/authreinforce");
        return;
      }
      util.log("Redirect after login: ", context.state.redirectAfterLogin);
      let redirRoute = simplifyRoute(context.state.redirectAfterLogin);
      context.commit("setRedirect", "");
      const companies = this.state.userCompanies || [];
      if (redirRoute?.params?.companyID && context.getters.validCompanies) {
        const companyID = Number(redirRoute.params.companyID);
        const company = companies.find((c) => c.id === companyID);
        await context.dispatch("selectCompany", company);
      } else if (companies.length === 1) {
        await context.dispatch("selectCompany", companies[0]);
      }
      if (!redirRoute) {
        redirRoute = simplifyRoute(context.state.selectedCompany ? getUserStartPage() : "/companyCatalog");
      }
      util.log("Pushing new route", { from: router.currentRoute, to: redirRoute });
      if (redirRoute && !routeEquals(redirRoute, router.currentRoute) && !router.currentRoute.meta?.avoidRedirect) {
        await router.replace(redirRoute.fullPath);
      } else {
        util.log("Redirect failed, to is null, routes are equal or avoidRedirect is marked in from");
      }
    },
    async logout({ commit }): Promise<void> {
      util.log("Action: logout");
      await backend.logout();
      commit("setUser", { logout: true });
    },
    async refreshCompanies(context) {
      util.log("Action: refreshCompanies");
      if (context.state.currentUser) {
        context.commit("setCompanies", { loading: true });
        try {
          const companies = await backend.getMyCompanies();
          context.commit("setCompanies", { companies });
        } catch (error) {
          util.logError("getMyCompanies", error);
          context.commit("setCompanies", { error });
        }
      } else {
        context.commit("setCompanies", { companies: null, loading: false });
      }
    },
    async selectCompany(context, company: Company | null) {
      util.log("Action: selectCompany", { company });
      context.commit("setCompany", { company, loading: true });
      await context.dispatch("refreshCompany");
    },
    async refreshCompany(context) {
      util.log("Action: refreshCompany");
      const cpy = context.state.selectedCompany;
      if (cpy) {
        context.commit("setCompany", { loading: true });
        try {
          const company = await backend.getCompanyData(cpy.id);
          context.commit("setCompany", { company });
        } catch (error) {
          util.logError("getCompanyData", error);
          context.commit("setCompany", { error });
        }
      } else {
        context.commit("setCompany", { loading: false });
      }
    },
    changeLang({ commit }, langKey: langKey) {
      util.log("Action: refreshCompany", { langKey });
      localStorage.setItem("langKey", langKey);
      commit("changeLang", getLangData(langKey));
    }
  },
  getters: {
    isInCompany(state): boolean {
      return !!state.currentUser && !!state.selectedCompany;
    },
    isLoadingUser(state): boolean {
      return state.currentUserLoading || state.selectedCompanyLoading;
    },
    validCompanies(state): boolean {
      return !!state.userCompanies && !state.userCompaniesLoading;
    },
    userPermission: (state, getters): PartialPermission => {
      return createPermission(state, getters);
    }
  },
  modules: {}
};

function createPermission(state: RootState, getters: WebsiteGetters): PartialPermission {
  const result: PartialPermission = {
    user: state.currentUser,
    company: state.selectedCompany,
    branchIDs: [],
    isLoading: getters.isLoadingUser,
    isLogged: !!state.currentUser,
    adminCompanies: [],
    isAdminOfAny: false,
    isGlobalAdmin: false,
    admin: false,
    allowConnectionGraph: false,
    allowTxnStatistics: false,
    paymentLink: false,
    clientAPI: false,
    txnModification: false,
    vendingAdmin: false,
    topayAdmin: false,
    txnView: true,
    euroshellCertificate: false,
    isReinforced:
      !!state.currentUser?.claims?.isToken ||
      !state.currentUser?.needsReinforced ||
      !!state.currentUser?.claims?.reinforcedLogin
  };
  if (!result.user) return result;
  result.isGlobalAdmin = result.user.isGlobalAdmin;
  if (result.user.permissions) {
    result.adminCompanies = result.user.permissions
      .filter((p) => p.admin && p.companyID != null)
      .map((p) => p.companyID ?? 0);
    result.isAdminOfAny = result.adminCompanies.length > 0;
  }
  assignCompanyPermissions(state, getters, result);
  return result;
}

function assignCompanyPermissions(state: RootState, getters: WebsiteGetters, result: PartialPermission) {
  if (!result.user) throw new Error("Invalid user permission");
  if (result.isGlobalAdmin) {
    result.isAdminOfAny = true;
    result.admin = true;
    PERMISSION_ATTRS.forEach((attr) => (result[attr] = true));
    if (getters.validCompanies) {
      result.adminCompanies = (state.userCompanies || []).map((c) => c.id);
    }
    return;
  }
  if (result.company == null || util.isEmpty(result.user.permissions)) {
    return;
  }
  const companyP = result.user.permissions.find((cpyPerm) => result.company && cpyPerm.companyID === result.company.id);
  if (!companyP) {
    return;
  }
  result.admin = companyP.admin;
  PERMISSION_ATTRS.forEach((attr) => {
    if (attr === "euroshellCertificate" || SPECIAL_PERMISSION_ATTRS.includes(attr)) {
      result[attr] = companyP[attr] || false;
    } else {
      result[attr] = result.admin || companyP[attr];
    }
  });
  result.branchIDs = companyP.branchIDs || [];
  if (isNull(companyP.txnView)) {
    result.txnView = true;
  }
}

function sortBranchesInCompanies(...someCompanies: (Company | null)[]) {
  someCompanies.forEach((c: Company | null) => {
    if (c?.branches) {
      c.branches = auxSortByName(c.branches);
      c.branches?.forEach((b) => {
        if (b.terminals) {
          b.terminals = auxSortByName(b.terminals);
        }
      });
    }
  });
}

function auxSortByName<T extends { name: string }>(arr: T[] | null | undefined): T[] {
  if (!arr) {
    return [];
  }
  return lodash(arr)
    .sortBy((c) => c.name)
    .value();
}

export default new Vuex.Store<RootState>(storeOptions);
